/** One catalogue product's settings drawer (plan §3.9): the registry-generated settings with
 *  their effective values and sources, the package composition, the outputs with their
 *  coupling and the run history (the itinerary planner's latest run and accepted-plan flags,
 *  each run's settings, and the diff of two runs).
 *
 *  Edits collect into one draft and save in ONE PATCH — all or nothing on the server. A
 *  refusal (400 `{code, detail, errors[]}`) maps each registry error onto its field (option
 *  keys per option); reachability refusals arrive the same way, with the reason. A
 *  `package.mode` change may answer `package_membership_sticky`, shown after the save.
 *
 *  `tour.join` (plan 2026-10-11 product joins §9): a draft that sets or changes it shows what the
 *  save would do under its row (`JoinPanel`: the IDs, who holds them, the required answers); Save
 *  waits for them and carries them as `join_consent`. After the save, what the join did is shown
 *  (`join` of the answer), the booking-move tasks linked. A product others join names them there
 *  (`joined_by`, `JoinedByNote`); a cleared join waiting for the product's next sync says what that
 *  sync splits off (`pending_split_off`, `PendingSplitOffNote`). */
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type {
  CatalogueOutput,
  CatalogueRegistry,
  CatalogueScope,
  CatalogueWarning,
  JoinOutcome,
} from '@/api/catalogue';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';

import { AiStatusSummary } from './AiStatus';
import {
  draftCount,
  draftSet,
  draftUnset,
  EMPTY_DRAFT,
  explainTargetInUse,
  KEY,
  mapSettingErrors,
  patchOf,
  pendingTone,
  specsByKey,
  valueLabel,
  type SettingsDraft,
} from './catalogueModel';
import { CouplingDialog } from './CouplingDialog';
import { JoinedByNote, PendingSplitOffNote, useJoinerRows } from './JoinNotes';
import { JoinOutcomeLines } from './JoinOutcome';
import { JoinPanel, useJoinDraft } from './JoinPanel';
import { OutputsPanel } from './OutputsPanel';
import { PackagePanel } from './PackagePanel';
import { RunDetailDialog } from './RunDetailDialog';
import { RunsTable } from './RunsTable';
import { useCatalogueProduct, usePatchProductSettings } from './queries';
import { SettingsForm } from './SettingsForm';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

interface DrawerTab {
  id: string;
  label: string;
  render: () => ReactNode;
}

export interface ProductSettingsDrawerProps {
  tenantId: number;
  productId: number;
  registry: CatalogueRegistry;
  onClose: () => void;
  /** Switch the drawer to another catalogue product (a package's leg). */
  onOpenProduct: (productId: number) => void;
  /** The integration has a dry-run build (re-couple preview). */
  previewSupported?: boolean;
  /** The page's catalogue scope: its tenant code labels the product pickers' options. */
  scope?: CatalogueScope;
}

export function ProductSettingsDrawer({
  tenantId,
  productId,
  registry,
  onClose,
  onOpenProduct,
  previewSupported = false,
  scope,
}: ProductSettingsDrawerProps) {
  const { show } = useSnackbar();
  const query = useCatalogueProduct(tenantId, productId);
  const product = query.data;
  const specs = specsByKey(registry);

  const [tab, setTab] = useState('settings');
  const [draft, setDraft] = useState<SettingsDraft>(EMPTY_DRAFT);
  const [note, setNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<CatalogueWarning[]>([]);
  const [joined, setJoined] = useState<{ outcome: JoinOutcome; intoName: string | null } | null>(null);
  const [coupling, setCoupling] = useState<CatalogueOutput | null>(null);
  const [runOpen, setRunOpen] = useState<number | null>(null);
  const [confirmClose, setConfirmClose] = useState<null | (() => void)>(null);

  const dirty = draftCount(draft);
  const join = useJoinDraft(tenantId, product, draft);
  // The product's own catalogue (restech and account): a product picker searches it, and the
  // products joining this one are looked up there.
  const productScope: CatalogueScope | null = product
    ? {
        tenantId,
        integration: product.restech,
        account: product.account || null,
        tenantCode: scope?.tenantCode ?? null,
      }
    : null;
  const products = product && productScope ? { scope: productScope, selfId: product.id } : undefined;
  // The products joining this one, named in the note under its join and in a refusal.
  const joinerRows = useJoinerRows(product, productScope);
  const patch = usePatchProductSettings(tenantId);

  const discard = () => {
    setDraft(EMPTY_DRAFT);
    setNote('');
    setFieldErrors({});
    setGeneral([]);
    join.reset();
  };
  const guarded = (then: () => void) => () => (dirty ? setConfirmClose(() => then) : then());

  const save = () => {
    setFieldErrors({});
    setGeneral([]);
    setWarnings([]);
    setJoined(null);
    const intoName = join.preview?.target.name ?? null;
    patch.mutate(
      { productId, body: { ...patchOf(draft, note), ...(join.consent ? { join_consent: join.consent } : {}) } },
      {
        onSuccess: (r) => {
          discard();
          setWarnings(r.warnings);
          if (r.join) setJoined({ outcome: r.join, intoName });
          show(r.changed ? 'Settings saved.' : 'Nothing changed.', 'success');
        },
        onError: (e) => {
          // An owner may have changed since the preview: ask it again.
          join.refetch();
          const refusals = catalogueApi.settingErrorsOf(e);
          if (refusals?.length) {
            // Products join this one's tour (join_target_in_use): named, with what to do first.
            const mapped = mapSettingErrors(
              explainTargetInUse(
                refusals,
                (id) => (id === product?.id ? product.joined_by : undefined),
                (v) => joinerRows.get(v),
              ),
            );
            setFieldErrors(mapped.fields);
            setGeneral(mapped.general);
          } else {
            setGeneral([(e as Error).message]);
          }
        },
      },
    );
  };

  const tabs: DrawerTab[] = product
    ? [
        {
          id: 'settings',
          label: 'Settings',
          render: () => (
            <>
              {product.resolution.problems.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Settings the resolver skipped</AlertTitle>
                  The next value applied instead; a sync never fails on these.
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {product.resolution.problems.map((p, i) => (
                      <li key={i}>
                        <code>{p.key ?? '—'}</code>
                        {p.option_id ? ` (option ${p.option_id})` : ''}: {p.message}
                        {p.source ? ` — from ${p.source}` : ''}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
              <SettingsForm
                tenantId={tenantId}
                product={product}
                specs={registry.settings}
                draft={draft}
                onSet={(key, optionId, value) => setDraft((d) => draftSet(d, key, optionId, value))}
                onUndo={(key, optionId) => setDraft((d) => draftUnset(d, key, optionId))}
                errors={fieldErrors}
                products={products}
                rowExtras={{
                  [KEY.join]: (
                    <>
                      <JoinedByNote
                        product={product}
                        joinerRows={joinerRows}
                        onOpenProduct={(id) => guarded(() => onOpenProduct(id))()}
                      />
                      {productScope && (
                        <PendingSplitOffNote product={product} scope={productScope} joining={join.target !== null} />
                      )}
                      <JoinPanel
                        join={join}
                        product={product}
                        integration={product.restech}
                        grainSpec={specs.get(KEY.grain)}
                        onResetGrain={() => setDraft((d) => draftSet(d, KEY.grain, null, null))}
                        onCancel={() => setDraft((d) => draftUnset(d, KEY.join, null))}
                      />
                    </>
                  ),
                }}
                disabled={patch.isPending}
                groupExtras={
                  product.package
                    ? {
                        package: (
                          <PackagePanel
                            pkg={product.package}
                            kindSpec={specs.get(KEY.kind)}
                            grainSpec={specs.get(KEY.grain)}
                            onOpenProduct={(id) => guarded(() => onOpenProduct(id))()}
                          />
                        ),
                      }
                    : {}
                }
              />
            </>
          ),
        },
        {
          id: 'outputs',
          label: `Outputs (${product.outputs.length})`,
          render: () => <OutputsPanel outputs={product.outputs} onCoupling={setCoupling} />,
        },
        {
          id: 'runs',
          label: `Runs (${product.run_count})`,
          render: () => (
            <>
              <AiStatusSummary ai={product.ai} onOpenRun={setRunOpen} />
              <RunsTable tenantId={tenantId} productId={productId} onOpen={(r) => setRunOpen(r.id)} />
            </>
          ),
        },
      ]
    : [];
  const active = tabs.find((t) => t.id === tab) ?? tabs[0];

  const drawer = (
    <Drawer
      anchor="right"
      open
      onClose={guarded(onClose)}
      // Clears the AppBar (lessons.md: Drawer + zIndex.modal).
      sx={{ zIndex: (t) => t.zIndex.modal }}
      PaperProps={{ sx: { width: { xs: '100vw', md: 760 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2" sx={{ overflowWrap: 'anywhere' }}>
              {product ? product.title || product.name : 'Catalogue product'}
            </Typography>
            {product && (
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary' }}>
                  {product.vendor_id}
                </Typography>
                {product.reference && (
                  <Typography variant="caption" color="text.secondary">
                    · {product.reference}
                  </Typography>
                )}
                <Chip size="small" label={valueLabel(specs.get(KEY.kind), product.kind?.value)} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                {product.removed_ts && <Chip size="small" label="removed upstream" color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                {product.pending_change && (
                  <Chip
                    size="small"
                    label={`upstream: ${product.pending_change.status}`}
                    color={pendingTone(product.pending_change.status)}
                    variant="outlined"
                    sx={{ height: 20, fontSize: 11 }}
                  />
                )}
                {product.name !== product.title && product.title && (
                  <Typography variant="caption" color="text.secondary">
                    listed as “{product.name}”
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <IconButton aria-label="Close" size="small" onClick={guarded(onClose)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {product && (
          <Tabs value={active?.id ?? false} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, mt: 0.5 }}>
            {tabs.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} sx={{ minHeight: 36, py: 0.5 }} />
            ))}
          </Tabs>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {query.isLoading && <CircularProgress size={24} aria-label="Loading the product" />}
        {query.isError && <Alert severity="error">{(query.error as Error).message}</Alert>}
        {joined && (
          <Alert
            severity={joined.outcome.booking_moves?.errors.length ? 'warning' : joined.outcome.deferred ? 'info' : 'success'}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            onClose={() => setJoined(null)}
          >
            <AlertTitle>{joined.outcome.deferred ? 'Join saved' : 'Joined'}</AlertTitle>
            <JoinOutcomeLines
              outcome={joined.outcome}
              name={product ? product.title || product.name : joined.outcome.vendor_id}
              intoName={joined.intoName ?? undefined}
            />
          </Alert>
        )}
        {warnings.map((w) => (
          <Alert key={`${w.code}-${w.product_id}`} severity="warning" sx={{ mb: 2 }} onClose={() => setWarnings([])}>
            {w.detail}
          </Alert>
        ))}
        {general.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Nothing was saved</AlertTitle>
            {general.map((g, i) => (
              <div key={i}>{g}</div>
            ))}
          </Alert>
        )}
        {Object.keys(fieldErrors).length > 0 && general.length === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Nothing was saved</AlertTitle>
            {Object.values(fieldErrors).map((detail, i) => (
              <div key={i}>{detail}</div>
            ))}
          </Alert>
        )}
        {active?.render()}
      </Box>

      {product && active?.id === 'settings' && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ flex: '1 1 220px' }}
          />
          <Typography variant="body2" color={dirty ? 'warning.main' : 'text.secondary'} sx={{ minWidth: 120 }}>
            {patch.isPending ? 'Saving…' : dirty ? `${dirty} unsaved change${dirty === 1 ? '' : 's'}` : 'No changes'}
          </Typography>
          {dirty > 0 && join.waitingFor && (
            <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', order: 1 }}>
              {join.waitingFor}
            </Typography>
          )}
          <Button onClick={discard} disabled={!dirty || patch.isPending}>
            Discard
          </Button>
          <Button variant="contained" onClick={save} disabled={!dirty || patch.isPending || join.waitingFor !== null}>
            Save
          </Button>
        </Box>
      )}
    </Drawer>
  );

  return (
    <>
      {drawer}
      {/* Siblings of the drawer, never children: a dialog inside a drawer is aria-hidden. */}
      {coupling && product && (
        <CouplingDialog
          tenantId={tenantId}
          output={coupling}
          productName={product.title || product.name}
          previewSupported={previewSupported}
          onClose={() => setCoupling(null)}
        />
      )}
      {runOpen !== null && (
        <RunDetailDialog tenantId={tenantId} runId={runOpen} registry={registry} onClose={() => setRunOpen(null)} />
      )}
      <ConfirmDialog
        open={confirmClose !== null}
        title="Discard unsaved changes?"
        message={`${dirty} setting change${dirty === 1 ? ' is' : 's are'} not saved.`}
        confirmLabel="Discard"
        destructive
        onCancel={() => setConfirmClose(null)}
        onConfirm={() => {
          const then = confirmClose;
          setConfirmClose(null);
          discard();
          then?.();
        }}
      />
    </>
  );
}
